import { dashboardSectionBandPadding } from "@/lib/dashboardToolbarUi";
import { CONTENT_MAX_WIDTH } from "@/lib/useDeviceType";
import React, { useRef } from "react";
import { FlatList, TouchableOpacity, View } from "react-native";
import { SizableText, XStack, YStack } from "tamagui";
import { SectionTitle } from "@/components/ui/Typography";
import { Collection } from "@/types";
import { CarouselArrowButton } from "./horizontalCarousel/CarouselArrowButton";
import { HORIZONTAL_CAROUSEL_MAX_ITEMS } from "./horizontalCarousel/constants";
import { useCarouselLeftPadding } from "./horizontalCarousel/useCarouselLeftPadding";
import { useHorizontalCarouselScroll } from "./horizontalCarousel/useHorizontalCarouselScroll";

interface FullWidthSectionProps {
  title: string;
  data: Collection[];
  renderItem: ({ item }: { item: Collection }) => React.ReactElement;
  onSeeAll?: () => void;
  seeAllText?: string;
}

export const FullWidthSection = React.memo(function FullWidthSection({
  title,
  data,
  renderItem,
  onSeeAll,
  seeAllText = "See All",
}: FullWidthSectionProps) {
  const onSeeAllRef = useRef(onSeeAll);
  onSeeAllRef.current = onSeeAll;
  const leftPadding = useCarouselLeftPadding();

  const displayData =
    data.length > HORIZONTAL_CAROUSEL_MAX_ITEMS
      ? data.slice(0, HORIZONTAL_CAROUSEL_MAX_ITEMS)
      : data;

  const {
    listRef,
    isAtStart,
    isAtEnd,
    handleLayout,
    handleContentSizeChange,
    handleScroll,
    scrollToDirection,
    stableFlatListRenderItem,
    buttonSize,
    iconSize,
    itemGap,
    contentPaddingVertical,
  } = useHorizontalCarouselScroll(displayData.length, renderItem);

  const shouldShowSeeAll = !!onSeeAll && data.length > 0;

  return (
    <YStack
      {...dashboardSectionBandPadding}
      gap="$1"
      bg="white"
      position="relative"
    >
      <YStack w="100%" maw={CONTENT_MAX_WIDTH} mx="auto" px={16}>
        <XStack ai="center" gap={12} minWidth={0}>
          <YStack f={1} minWidth={0}>
            <SectionTitle
              color="$color"
              numberOfLines={2}
              ellipsizeMode="tail"
            >
              {title}
            </SectionTitle>
          </YStack>
          {shouldShowSeeAll && (
            <TouchableOpacity
              onPress={() => onSeeAllRef.current?.()}
              style={{ flexShrink: 0, cursor: "pointer" } as object}
            >
              <SizableText
                size="$4"
                $gtSm={{ size: "$5" }}
                fontWeight="600"
                color="#987451"
              >
                {seeAllText}
              </SizableText>
            </TouchableOpacity>
          )}
        </XStack>
      </YStack>

      <XStack ai="center" position="relative">
        {!isAtStart && (
          <CarouselArrowButton
            direction="left"
            buttonSize={buttonSize}
            iconSize={iconSize}
            onPress={() => scrollToDirection("left")}
          />
        )}

        <FlatList
          ref={listRef}
          onLayout={handleLayout}
          horizontal
          showsHorizontalScrollIndicator={false}
          nestedScrollEnabled
          data={displayData}
          renderItem={stableFlatListRenderItem}
          keyExtractor={(item, index) => `${item.id || `item-${index}`}`}
          contentContainerStyle={{
            paddingLeft: leftPadding,
            paddingRight: 16,
            paddingVertical: contentPaddingVertical,
          }}
          onContentSizeChange={handleContentSizeChange}
          onScroll={handleScroll}
          scrollEventThrottle={16}
          ItemSeparatorComponent={() => <View style={{ width: itemGap }} />}
        />

        {!isAtEnd && (
          <CarouselArrowButton
            direction="right"
            buttonSize={buttonSize}
            iconSize={iconSize}
            onPress={() => scrollToDirection("right")}
          />
        )}
      </XStack>
    </YStack>
  );
});
